import Route from '@ember/routing/route';
import { inject as service } from '@ember/service';
import { isNone } from '@ember/utils';

import { ErrorSeverity } from 'later/services/errors';
import redirect from 'shared/utils/redirect';

import type RouterService from '@ember/routing/router-service';
import type StoreService from '@ember-data/store';
import type SessionService from 'later/services/-private/session';
import type AuthService from 'later/services/auth';
import type CacheService from 'later/services/cache';
import type ErrorsService from 'later/services/errors';
import type { Transition } from 'shared/types';

export default class IndexRoute extends Route {
  @service declare auth: AuthService;
  @service declare cache: CacheService;
  @service declare session: SessionService;
  @service declare router: RouterService;
  @service declare errors: ErrorsService;
  @service declare store: StoreService;

  beforeModel(transition: Transition): void {
    this.session.requireAuthentication(transition, 'user.login');
  }

  redirect(): void {
    if (!this.auth.currentAccount) {
      this.auth.logout.perform();
    }

    const cachedGroupId = this.cache.retrieve<string>('last_group_id');
    const cachedGroup = cachedGroupId && this.store.peekRecord('group', cachedGroupId);
    const group = cachedGroup ? cachedGroup : this.auth.lastGroup;
    const groupSlug = group?.slug ?? '';

    if (isNone(group)) {
      this.handleMissingGroup();
    } else if (group.contributor) {
      this.router.transitionTo('cluster.collect.contribute', groupSlug);
    } else if (this.session.isSeamlessCheckout && this.session.plan) {
      this.router.transitionTo('plans.plan', this.session.plan);
    } else if (this.session.hasRedirectUrl) {
      const { redirectUrl } = this.session;
      this.session.clearRedirectUrl();
      redirect(redirectUrl);
    } else if (this.session.isCreatorHomeRedirect) {
      this.router.transitionTo('cluster.partnerships', groupSlug);
    } else if (
      this.auth.currentAccount?.experiments?.['web_custom-analytics_feature-trial'] === 'custom-analytics-trial'
    ) {
      this.router.transitionTo('cluster.custom-analytics', groupSlug);
    } else {
      this.router.transitionTo('cluster.schedule.calendar', groupSlug);
    }
  }

  private handleMissingGroup(): void {
    try {
      this.errors.log(
        'No group found, index route',
        {
          localGroupIds: this.store.peekAll('group').mapBy('id'),
          lastGroupId: this.cache.retrieve('last_group_id') || 'none',
          localStorage: { ...window.localStorage }
        },
        ErrorSeverity.Info
      );
    } catch (error) {
      this.errors.log('Could not log nogroup error, index route', { error }, ErrorSeverity.Info);
    }

    this.router.transitionTo('nogroup');
  }
}
